import React, { useEffect, useState, useRef } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { API, downloadFile, openFile } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Btn, TextInput, Loader, EmptyState } from "../components/Primitives";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

const STATUS_META = {
  pass: { color: "#059669", bg: "rgba(5,150,105,0.10)", icon: "CheckCircle2", label: "Pass" },
  warn: { color: "#D97706", bg: "rgba(217,119,6,0.10)", icon: "AlertTriangle", label: "Warn" },
  fail: { color: "#DC2626", bg: "rgba(220,38,38,0.10)", icon: "XCircle", label: "Fail" },
  info: { color: "#0891B2", bg: "rgba(8,145,178,0.10)", icon: "Info", label: "Info" },
};
const CATEGORY_META = {
  tls: { label: "TLS", icon: "Lock" },
  privacy_notice: { label: "Privacy Notice", icon: "FileText" },
  cookie_consent: { label: "Cookie Consent", icon: "Cookie" },
  cookie_policy: { label: "Cookie Policy", icon: "FileCog" },
  dpo_contact: { label: "DPO", icon: "UserCheck" },
  grievance: { label: "Grievance Redressal", icon: "Scale" },
  rights_cta: { label: "Rights CTA", icon: "Users" },
  form_consent: { label: "Form Consent", icon: "ClipboardCheck" },
  third_party: { label: "Third-Party Processors", icon: "Share2" },
  consent_deep_dive: { label: "Consent Deep-Dive", icon: "Languages" },
  cross_border: { label: "Cross-border", icon: "Globe" },
  childrens_data: { label: "Children's Data", icon: "Baby" },
  dark_patterns: { label: "Dark Patterns", icon: "AlertOctagon" },
  security_headers: { label: "Security Headers", icon: "ShieldCheck" },
};

const KIND_META = {
  deep_dive: { label: "Deep-Dive", color: "#7C3AED", bg: "rgba(124,58,237,0.10)" },
  new:       { label: "New",       color: "#0891B2", bg: "rgba(8,145,178,0.10)" },
  bonus:     { label: "Bonus",     color: "#D97706", bg: "rgba(217,119,6,0.10)" },
};
const CHECK_STATUS_META = {
  pass:    { color: "#059669", icon: "CheckCircle2" },
  partial: { color: "#D97706", icon: "AlertTriangle" },
  fail:    { color: "#DC2626", icon: "XCircle" },
  info:    { color: "#64748B", icon: "MinusCircle" },
};
const CHECK_ICONS = {
  consent_banner: "Cookie", privacy_policy: "FileText", cookie_policy: "FileCog",
  grievance: "Scale", rights: "Users", dpo: "UserCheck",
  age_gate_language: "Languages", form_consent: "ClipboardCheck",
  third_party: "Share2", children: "Baby", multilang_consent: "Globe2",
};

function ScorecardPanel({ scorecard }) {
  if (!scorecard?.checks?.length) return null;
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }} data-testid="dpdp-scorecard">
      <div className="px-4 py-2.5 flex items-center justify-between" style={{ background: "var(--bg-2)", borderBottom: "1px solid var(--border)" }}>
        <div className="flex items-center gap-2">
          <Icons.Gauge className="w-4 h-4" style={{ color: "var(--primary)" }} />
          <span className="text-[12.5px] font-semibold ink">DPDP Compliance Scorecard</span>
        </div>
        <div className="text-[11.5px] font-semibold tnum" data-testid="scorecard-total">
          {scorecard.bonus_points > 0 ? (
            <>
              <span className="ink">{scorecard.base_score}/100</span>
              <span className="ml-1" style={{ color: "#D97706" }}>+{scorecard.bonus_points} bonus</span>
              <span className="muted"> → </span>
              <span style={{ color: "var(--primary)" }}>{scorecard.total_score}/{scorecard.max_score}</span>
            </>
          ) : (
            <span style={{ color: "var(--primary)" }}>{scorecard.total_score}/{scorecard.max_score}</span>
          )}
        </div>
      </div>
      <div className="divide-y" style={{ borderColor: "var(--border)" }}>
        {scorecard.checks.map((c) => {
          const sm = CHECK_STATUS_META[c.status] || CHECK_STATUS_META.info;
          const km = KIND_META[c.kind];
          const Ico = Icons[CHECK_ICONS[c.key]] || Icons.Circle;
          const StIco = Icons[sm.icon] || Icons.Circle;
          return (
            <div key={c.key} className="px-4 py-2.5 flex items-start gap-3" style={{ borderTop: "1px solid var(--border)" }} data-testid={`scorecard-check-${c.key}`}>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${sm.color}14` }}>
                <Ico className="w-3.5 h-3.5" style={{ color: sm.color }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[12.5px] font-semibold ink">{c.label}</span>
                  {km && (
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-[1px] rounded" style={{ background: km.bg, color: km.color }}>
                      {km.label}
                    </span>
                  )}
                  <span className="text-[9.5px] mono muted">{c.dpdp_ref}</span>
                </div>
                <div className="text-[11px] muted mt-0.5">{c.description}</div>
                {c.note && <div className="text-[10.5px] mt-0.5" style={{ color: sm.color }}>{c.note}</div>}
              </div>
              <div className="text-right shrink-0">
                <div className="flex items-center gap-1 justify-end">
                  <StIco className="w-3.5 h-3.5" style={{ color: sm.color }} />
                  {c.points_max > 0 ? (
                    <span className="text-[13px] font-bold tnum" style={{ color: sm.color }} data-testid={`scorecard-pts-${c.key}`}>
                      {c.points_awarded}<span className="text-[10px] muted font-medium">/{c.points_max}</span>
                    </span>
                  ) : (
                    <span className="text-[10px] font-semibold uppercase" style={{ color: sm.color }}>{c.status}</span>
                  )}
                </div>
                {c.kind === "scored" && <div className="text-[9px] muted mt-0.5">{c.points_max} points</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GradeBadge({ grade, score, large }) {
  const c = grade === "A" ? "#059669" : grade === "B" ? "#10B981" : grade === "C" ? "#D97706" : grade === "D" ? "#EA580C" : "#DC2626";
  return (
    <div className={`inline-flex items-center gap-2 rounded-lg font-bold ${large ? "px-4 py-2 text-2xl" : "px-2 py-1 text-sm"}`}
      style={{ background: `${c}15`, color: c, border: `1px solid ${c}40` }}>
      <span>{grade}</span>
      <span className={`opacity-80 font-medium ${large ? "text-sm" : "text-[10.5px]"}`}>({score ?? "—"}/100)</span>
    </div>
  );
}

function FindingRow({ f }) {
  const sm = STATUS_META[f.status] || STATUS_META.info;
  const cm = CATEGORY_META[f.category] || { label: f.category, icon: "Circle" };
  const Ico = Icons[sm.icon] || Icons.Circle;
  const CatIco = Icons[cm.icon] || Icons.Circle;
  const [open, setOpen] = useState(false);
  const canExpand = !!f.evidence && Object.keys(f.evidence || {}).length > 0;
  return (
    <div className="rounded-lg p-3 card-hover" style={{ border: "1px solid var(--border)" }} data-testid={`finding-${f.check_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: sm.bg }}>
          <Ico className="w-4 h-4" style={{ color: sm.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <span className="text-[13px] font-semibold ink">{f.title}</span>
            <span className="text-[9.5px] font-medium uppercase tracking-wider px-1.5 py-[1px] rounded" style={{ background: sm.bg, color: sm.color }}>
              {sm.label} · {f.severity}
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] muted">
              <CatIco className="w-3 h-3" /> {cm.label}
            </span>
            {f.dpdp_ref && <span className="text-[9.5px] mono muted">{f.dpdp_ref}</span>}
          </div>
          <div className="text-[11.5px] muted">{f.recommendation}</div>
          {canExpand && (
            <button onClick={() => setOpen(!open)} className="text-[10.5px] primary mt-1 font-medium hover:underline" data-testid={`finding-detail-${f.check_id}`}>
              {open ? "Hide" : "Show"} evidence
            </button>
          )}
          {open && (
            <pre className="mt-2 p-2 rounded text-[10.5px] mono overflow-x-auto" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }}>
              {JSON.stringify(f.evidence, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}


function ScanDetail({ scan, onClose, onDelete, apiBase }) {
  if (!scan) return null;
  const grouped = {};
  (scan.findings || []).forEach((f) => { (grouped[f.category] ||= []).push(f); });
  const orderedCats = Object.keys(CATEGORY_META).filter((c) => grouped[c]);
  const openReport = () => downloadFile(`/scanner/scans/${scan.id}/report.pdf`, `dpdp_scan_${scan.id.slice(0, 8)}.pdf`);
  const openScreenshot = () => openFile(`/scanner/scans/${scan.id}/screenshot`);

  return (
    <Dialog open={!!scan} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="scan-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Icons.ScanLine className="w-4 h-4" style={{ color: "var(--primary)" }} />
            Scan · {scan.url}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Scanned at {new Date(scan.completed_at || scan.requested_at).toLocaleString()} · load {scan.load_time_ms}ms
          </DialogDescription>
        </DialogHeader>

        {scan.status !== "completed" ? (
          <div className="py-6"><Loader /><div className="text-center text-[11.5px] muted mt-2">Scan is {scan.status}…</div></div>
        ) : (
          <div className="space-y-4 max-h-[75vh] overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg text-center" style={{ border: "1px solid var(--border)" }}>
                <div className="label-cap mb-1">Score</div>
                <GradeBadge grade={scan.grade} score={scan.score} large />
              </div>
              {["pass", "warn", "fail", "info"].map((s) => {
                const m = STATUS_META[s];
                const Ico = Icons[m.icon];
                return (
                  <div key={s} className="p-3 rounded-lg" style={{ border: "1px solid var(--border)", background: m.bg }}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <Ico className="w-3.5 h-3.5" style={{ color: m.color }} />
                      <div className="text-[10.5px] font-semibold uppercase tracking-wider" style={{ color: m.color }}>{m.label}</div>
                    </div>
                    <div className="num-display text-2xl tnum" style={{ color: m.color }}>{scan.counts?.[s] || 0}</div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Btn size="sm" variant="secondary" onClick={openReport} data-testid="scan-pdf-btn">
                <Icons.FileDown className="w-3.5 h-3.5" /> PDF report
              </Btn>
              <Btn size="sm" variant="secondary" onClick={openScreenshot} data-testid="scan-screenshot-btn">
                <Icons.Image className="w-3.5 h-3.5" /> Screenshot
              </Btn>
              <Btn size="sm" variant="danger" onClick={() => onDelete(scan.id)} data-testid="scan-delete-btn">
                <Icons.Trash2 className="w-3.5 h-3.5" /> Delete
              </Btn>
              {scan.error && (
                <span className="text-[10.5px] danger inline-flex items-center gap-1">
                  <Icons.AlertCircle className="w-3.5 h-3.5" /> {scan.error}
                </span>
              )}
            </div>

            <ScorecardPanel scorecard={scan.scorecard} />

            {orderedCats.map((cat) => {
              const cm = CATEGORY_META[cat];
              const CatIco = Icons[cm.icon] || Icons.Circle;
              return (
                <div key={cat}>
                  <div className="flex items-center gap-2 mb-2">
                    <CatIco className="w-4 h-4" style={{ color: "var(--primary)" }} />
                    <div className="text-[12px] font-semibold ink">{cm.label}</div>
                    <div className="text-[10px] muted">({grouped[cat].length})</div>
                  </div>
                  <div className="space-y-1.5">
                    {grouped[cat].map((f) => <FindingRow key={f.check_id} f={f} />)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


export default function WebsiteScanner() {
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [activeScan, setActiveScan] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/scanner/scans?limit=100");
      setScans(data.items || []);
    } catch (e) {
      toast.error("Failed to load scans");
    }
    setLoading(false);
  };

  useEffect(() => { load(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  // Poll while any scan is running
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (scans.some((s) => s.status === "running")) {
      pollRef.current = setInterval(() => load(), 3000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [scans]);

  const startScan = async () => {
    if (!url.trim()) return toast.info("Enter a URL");
    setStarting(true);
    try {
      const { data } = await api.post("/scanner/scans", { url: url.trim(), label: label.trim() || null });
      toast.success("Scan started");
      setUrl(""); setLabel("");
      setScans((prev) => [data, ...prev]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed to start");
    }
    setStarting(false);
  };

  const openScan = async (id) => {
    try {
      const { data } = await api.get(`/scanner/scans/${id}`);
      setActiveScan(data);
    } catch (e) {
      toast.error("Failed to load scan");
    }
  };

  const deleteScan = async (id) => {
    if (!window.confirm("Delete this scan?")) return;
    try {
      await api.delete(`/scanner/scans/${id}`);
      setScans((prev) => prev.filter((s) => s.id !== id));
      setActiveScan(null);
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <PageHeader
        kicker="Assessment · Website Scanner"
        title="DPDP Website Scanner"
        description="Point the scanner at your public site. It grades a weighted DPDP scorecard — consent banner (25), privacy policy (20), cookie policy (15), grievance redressal (20), data subject rights (10), DPO (10) — plus deep-dive checks for age gates, 22 scheduled Indian languages, form consent, consent-gated third-party trackers, and bonus points for children's data provisions and multi-language consent."
        testId="scanner-page"
      />

      {/* Launch form */}
      <div className="surface rounded-2xl p-5 card-shadow card-hover stagger-in mb-4" style={{ border: "1px solid var(--border)", "--stagger": 0 }}>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_260px_auto] gap-3 items-end">
          <div>
            <label className="block text-xs font-medium ink mb-1.5">URL to scan</label>
            <TextInput value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://your-website.com" data-testid="scanner-url" onKeyDown={(e) => e.key === "Enter" && startScan()} />
          </div>
          <div>
            <label className="block text-xs font-medium ink mb-1.5">Label (optional)</label>
            <TextInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Marketing homepage" data-testid="scanner-label" />
          </div>
          <Btn onClick={startScan} disabled={starting || !url.trim()} data-testid="scanner-start">
            {starting ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : <Icons.PlayCircle className="w-4 h-4" />}
            Start scan
          </Btn>
        </div>
        <div className="mt-3 flex items-center gap-1 text-[10.5px] muted">
          <Icons.Info className="w-3 h-3" /> Scan typically completes in 10-30 seconds. Runs entirely on the server; no browser extension needed.
        </div>
      </div>

      {/* Scan history */}
      <div className="surface rounded-2xl card-shadow stagger-in" style={{ border: "1px solid var(--border)", "--stagger": 1 }}>
        <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
          <div>
            <div className="label-cap mb-0.5">History</div>
            <h3 className="text-[15px] font-semibold ink tracking-tight">Recent scans</h3>
          </div>
          <Btn variant="secondary" size="sm" onClick={load} data-testid="scanner-refresh">
            <Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Btn>
        </div>

        {loading ? <div className="p-6"><Loader /></div> : (
          scans.length === 0 ? (
            <div className="p-6">
              <EmptyState title="No scans yet"
                hint="Start your first scan above. The scanner will produce a graded report with findings mapped to DPDP Sec. 5/Sec. 6/Sec. 8/Sec. 9/Sec. 11-Sec. 14/Sec. 16." />
            </div>
          ) : (
            <div className="p-3">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {scans.map((s) => (
                  <div key={s.id} onClick={() => openScan(s.id)} className="p-3 rounded-lg cursor-pointer card-hover"
                    style={{ border: "1px solid var(--border)" }} data-testid={`scan-card-${s.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold ink truncate">{s.label || s.url}</div>
                        <div className="text-[11px] muted truncate mt-0.5 mono">{s.url}</div>
                      </div>
                      {s.status === "completed" ? (
                        <GradeBadge grade={s.grade} score={s.score} />
                      ) : s.status === "failed" ? (
                        <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded" style={{ background: "rgba(220,38,38,0.10)", color: "#DC2626" }}>
                          <Icons.XCircle className="w-3 h-3" /> Failed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded" style={{ background: "rgba(79,70,229,0.10)", color: "var(--primary)" }}>
                          <Icons.Loader2 className="w-3 h-3 animate-spin" /> Running
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-3 flex-wrap text-[10.5px] muted">
                      <span className="inline-flex items-center gap-1"><Icons.Clock className="w-3 h-3" /> {new Date(s.requested_at).toLocaleString()}</span>
                      {s.status === "completed" && (
                        <>
                          <span className="inline-flex items-center gap-1" style={{ color: STATUS_META.pass.color }}><Icons.CheckCircle2 className="w-3 h-3" /> {s.counts?.pass || 0}</span>
                          <span className="inline-flex items-center gap-1" style={{ color: STATUS_META.warn.color }}><Icons.AlertTriangle className="w-3 h-3" /> {s.counts?.warn || 0}</span>
                          <span className="inline-flex items-center gap-1" style={{ color: STATUS_META.fail.color }}><Icons.XCircle className="w-3 h-3" /> {s.counts?.fail || 0}</span>
                          <span className="inline-flex items-center gap-1" style={{ color: STATUS_META.info.color }}><Icons.Info className="w-3 h-3" /> {s.counts?.info || 0}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        )}
      </div>

      <ScanDetail scan={activeScan} onClose={() => setActiveScan(null)} onDelete={deleteScan} apiBase={API} />
    </div>
  );
}
